import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, Check, RefreshCw } from "lucide-react";
import { cn } from "@/lib/currencyFormatter";

interface NotificationHeaderProps {
  unreadCount: number;
  onMarkAllAsRead: () => void;
  onRefresh: () => void;
  isRefreshing: boolean;
}

export const NotificationHeader: React.FC<NotificationHeaderProps> = ({
  unreadCount,
  onMarkAllAsRead,
  onRefresh,
  isRefreshing
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-3">
        <div className="relative">
          <Bell className="h-8 w-8 text-blue-600" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center bg-red-500 text-white text-xs font-bold animate-pulse"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </Badge>
          )}
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("notifications.title")}</h1>
          <p className="text-gray-600">{t("notifications.subtitle")}</p>
        </div>
      </div>
      
      <div className="flex items-center space-x-2">
        {unreadCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={onMarkAllAsRead}
            className="text-green-600 border-green-200 hover:bg-green-50"
          >
            <Check className="h-4 w-4 mr-2" />
            {t("notifications.markAllAsRead")}
          </Button>
        )}
        
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={isRefreshing}
        >
          <RefreshCw className={cn("h-4 w-4 mr-2", isRefreshing && "animate-spin")} />
          {t("notifications.refresh")}
        </Button>
      </div>
    </div>
  );
}; 